// ── 리포트 계산 엔진 ──────────────────────────────────────────
// 화면(UI) 없이 숫자 계산만 담당. 월별 카드, 추후 시즌 정산 리포트가 공통으로 사용.
// 수익/지출 분류를 바꾸거나 추가할 땐 INCOME_CATS / EXPENSE_CATS와 classify 함수만 수정하면 됨.
//
// ⚠️ 날짜 처리 주의: 이 서비스는 한국(KST) 기준. toISOString()으로 날짜 문자열을 만들지 말 것.
//    (인수인계 문서 "⏰ 타임존/날짜 처리 원칙" 참고)

import { getPatchTransitionInfo } from "./bossData";
import { getArcaneLevelData } from "./symbolData";

// ── 수익 분류 (원 그래프 순서 = 이 배열 순서) ──
export const INCOME_CATS = [
  { key: "boss",   label: "보스 결정석", icon: "⚔️", color: "#7B6CF6" },
  { key: "hunt",   label: "사냥",        icon: "🏃", color: "#2FA36B" },
  { key: "drop",   label: "물욕템",      icon: "💎", color: "#E8A33D" },
  { key: "market", label: "경매장",      icon: "🏪", color: "#3D8BE8" },
  { key: "etc",    label: "기타",        icon: "🎁", color: "#9AA3AE" },
];

// ── 지출 분류 ──
export const EXPENSE_CATS = [
  { key: "star",       label: "스타포스",   icon: "⭐", color: "#E8A33D" },
  { key: "cube",       label: "큐브",       icon: "🎲", color: "#7B6CF6" },
  { key: "symbol",     label: "심볼업",     icon: "🌊", color: "#3D8BE8" },
  { key: "market",     label: "경매장 구매", icon: "🏪", color: "#2FA36B" },
  { key: "spell",      label: "주문서",     icon: "📜", color: "#D96C9A" },
  { key: "boss-entry", label: "보스 입장권", icon: "🎫", color: "#C9573F" },
  { key: "etc",        label: "기타",       icon: "📦", color: "#9AA3AE" },
];
const EXPENSE_KEYS = new Set(EXPENSE_CATS.map(c => c.key));

// ── 로컬(KST) 날짜 유틸 ──
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return ymd(d);
}
function daysInMonth(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}
export function prevMonthKey(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}
export function monthLabel(monthKey, withYear = true) {
  const [y, m] = monthKey.split("-").map(Number);
  return withYear ? `${y}년 ${m}월` : `${m}월`;
}

// ── 기록 묶음 → 요약 ──
// ctx: { favorites, includeSol, calcIncome, calcSolIncome }
//   calcIncome / calcSolIncome 은 App.js의 기존 함수를 그대로 넘겨받음
//   (캐릭터별 솔에르다·물욕템 A/B 설정을 기존 통계와 동일하게 반영하기 위함)
export function summarize(records, ctx) {
  const { favorites, includeSol, calcIncome, calcSolIncome } = ctx;
  const income  = Object.fromEntries(INCOME_CATS.map(c => [c.key, 0]));
  const expense = Object.fromEntries(EXPENSE_CATS.map(c => [c.key, 0]));
  let exchangeBuy = 0;   // 메포 → 메소 (메소 구매)
  let exchangeSell = 0;  // 메소 → 메포 (메소 판매)
  const dates = new Set();

  for (const r of records) {
    if (r.date) dates.add(r.date);

    if (r.kind === "income") {
      // 환전은 "번 돈"이 아니라 재화 교환이므로 파밍 수익에서 분리
      if (r.type === "exchange") { exchangeBuy += r.totalIncome || 0; continue; }

      const total = calcIncome(r, favorites);          // A/B 설정 반영된 기록 총액
      const sol   = calcSolIncome(r, favorites);       // 솔에르다(조각) 가치 (B유형 캐릭터는 0)
      const boss  = Math.min(r.bossIncome || 0, total);
      const rest  = Math.max(0, total - boss - sol);

      income.boss += boss;
      const restKey =
        r.type === "hunt"        ? "hunt"   :
        r.type === "drop"        ? "drop"   :
        r.type === "market-sell" ? "market" : "etc"; // 에픽던전·이벤트·보스 기록 속 메소 등
      income[restKey] += rest;
      if (includeSol) income.hunt += sol;              // "조각 가치 포함" 스위치
    } else if (r.kind === "expense") {
      if (r.type === "exchange") { exchangeSell += r.meso || 0; continue; }
      const key = EXPENSE_KEYS.has(r.type) ? r.type : "etc";
      expense[key] += r.meso || 0;
    }
  }

  const farmIncome = Object.values(income).reduce((s, v) => s + v, 0);
  const totalExpense = Object.values(expense).reduce((s, v) => s + v, 0);
  const topIncome = INCOME_CATS
    .map(c => ({ ...c, value: income[c.key] }))
    .sort((a, b) => b.value - a.value)[0];

  return {
    income, expense, farmIncome, totalExpense,
    net: farmIncome - totalExpense,
    exchangeBuy, exchangeSell,
    recordDays: dates.size,
    recordCount: records.length,
    topIncome: topIncome && topIncome.value > 0
      ? { ...topIncome, share: topIncome.value / farmIncome }
      : null,
  };
}

// ── 해당 월에 있었던 가격 패치 (bossData.js / symbolData.js 이력에서 자동 추출) ──
export function getMonthPatches(monthKey) {
  const out = [];
  const n = daysInMonth(monthKey);
  for (let day = 1; day <= n; day++) {
    const d = `${monthKey}-${String(day).padStart(2, "0")}`;
    const info = getPatchTransitionInfo(d);
    const bossParts = [info.daily && "일간", info.weekly && "주간", info.monthly && "월간"].filter(Boolean);
    const md = `${Number(monthKey.slice(5))}/${day}`;
    if (bossParts.length) out.push({ date: d, label: `${md} ${bossParts.join("·")}보스 결정석 가격 변경` });
    if (getArcaneLevelData(d) !== getArcaneLevelData(addDays(d, -1)))
      out.push({ date: d, label: `${md} 아케인 심볼 강화 비용 변경` });
  }
  return out;
}

// ── 월별 카드 데이터 ──
// 진행 중인 달은 "지난달 같은 기간(1일~오늘 날짜)"과 비교, 지난 달은 월 전체끼리 비교.
export function buildMonthlyCards(records, ctx, todayStr) {
  const byMonth = {};
  for (const r of records) {
    if (!r.date) continue;
    const k = r.date.slice(0, 7);
    (byMonth[k] = byMonth[k] || []).push(r);
  }
  const currentKey = todayStr.slice(0, 7);
  const keys = Object.keys(byMonth).sort().reverse();

  return keys.map(key => {
    const recs = byMonth[key];
    const summary = summarize(recs, ctx);
    const isCurrent = key === currentKey;
    const pKey = prevMonthKey(key);
    let prevRecs = byMonth[pKey] || [];
    let compareLabel = `${monthLabel(pKey, false)} 대비`;

    if (isCurrent) {
      const dayLimit = Math.min(Number(todayStr.slice(8, 10)), daysInMonth(pKey));
      prevRecs = prevRecs.filter(r => Number(r.date.slice(8, 10)) <= dayLimit);
      compareLabel = `${monthLabel(pKey, false)} 1~${dayLimit}일 대비`;
    }
    const prev = prevRecs.length ? summarize(prevRecs, ctx) : null;
    const pct = (cur, before) => (before > 0 ? ((cur - before) / before) * 100 : null);

    return {
      key, isCurrent, summary, prev, compareLabel,
      incomePct: prev ? pct(summary.farmIncome, prev.farmIncome) : null,
      netDiff: prev ? summary.net - prev.net : null,
      patches: getMonthPatches(key),
      throughDay: isCurrent ? Number(todayStr.slice(8, 10)) : null,
    };
  });
}
